import React, { useEffect } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SiteFooter } from '../components/SiteFooter';
import { REFERRAL_PROGRAM_URL, REFERRAL_DASHBOARD_URL, REFERRAL_TERMS_URL, REFERRAL_WAITLIST_MAILTO, referralEconomics, referralRules, referralSteps } from '../data/referral';
import { trackCustom } from '../utils/tracking';

/** A real link when configured, otherwise a clearly disabled "coming soon" state. */
function ProgramLink({
  href,
  label,
  primary = false
}: {
  href: string;
  label: string;
  primary?: boolean;
}) {
  const base = 'inline-flex items-center justify-center gap-2 px-5 py-3.5 font-display text-lg font-extrabold uppercase tracking-wide';
  if (!href) {
    return <span aria-disabled="true" className={`${base} cursor-not-allowed border border-white/15 text-steel-500`} data-id="element-1733" data-mp="ne8oymzuticq">
        {label} · Coming soon
      </span>;
  }
  return <a href={href} onClick={() => trackCustom('referral_link_clicked', {
    label
  })} className={`${base} transition-[background-color,color,transform] duration-150 ease-grit active:translate-y-[2px] ${primary ? 'bg-grit-500 text-ink-950 hover:bg-grit-400' : 'border-2 border-grit-500 text-grit-500 hover:bg-grit-500 hover:text-ink-950'}`} data-id="element-1734" data-mp="re0qwguqsq94">
      {label}
      <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1735" data-mp="9h6fhfpt771e" />
    </a>;
}
export function CrewRewards() {
  const live = Boolean(REFERRAL_PROGRAM_URL);
  useEffect(() => {
    window.scrollTo(0, 0);
    trackCustom('referral_page_visited');
  }, []);
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1736" data-mp="3qw3il8iing1p">
      <PageHeader data-id="element-1737" data-mp="3lx0atgb5ivq1" />
      <main data-id="element-1738" data-mp="3lxjryusz9g30">
        <section className="border-b border-white/10" data-id="element-1739" data-mp="3lxthkha41l86">
          <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:grid lg:grid-cols-[1.2fr_0.8fr] lg:items-end lg:gap-16 lg:px-8 lg:py-20" data-id="element-1740" data-mp="3hv58c8hhicao">
            <div data-id="element-1741" data-mp="3cwc6xefh2bqv">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1742" data-mp="3cxwqya45ucah">GritVue Crew Rewards</p>
              <h1 className="grit-headline mt-3 text-[clamp(2.75rem,9vw,5.5rem)] font-extrabold uppercase leading-[0.9]" data-id="element-1743" data-mp="379a0nb3spy2g">
                Share GritVue.
                <br data-id="element-1744" data-mp="2tbbpsfooojrp" />
                <span className="text-grit-500" data-id="element-1745" data-mp="2tbv3tlb7qo0g">Get paid.</span>
              </h1>
              <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-steel-300 sm:text-lg" data-id="element-1746" data-mp="2y93rg1f4rk93">
                Know someone who works in the dark? Send them your link. They save on their X1, you earn a reward when
                their order is complete.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-1747" data-mp="3c7l7dcysnsgg">
                <ProgramLink href={REFERRAL_PROGRAM_URL} label="Join Crew Rewards" primary data-id="element-1748" data-mp="3h7fynhmhek21" />
                <ProgramLink href={REFERRAL_DASHBOARD_URL} label="My dashboard" data-id="element-1749" data-mp="8s63yukx8h4a" />
              </div>
              {!live && <a href={REFERRAL_WAITLIST_MAILTO} onClick={() => trackCustom('referral_link_clicked', {
              label: 'waitlist'
            })} className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-1750" data-mp="djo3rfb7kbno">
                  Tell me when it opens
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1751" data-mp="22zhz829uc4eo" />
                </a>}
            </div>

            <dl className="mt-10 grid grid-cols-2 border border-white/12 lg:mt-0" data-id="element-1752" data-mp="1fapcmwf3z8wq">
              <div className="border-r border-white/12 p-5 sm:p-6" data-id="element-1753" data-mp="1ac4ausk7kh66">
                <dt className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1754" data-mp="1oalxemydnnmi">Your friend gets</dt>
                <dd className="mt-2 font-display text-5xl font-extrabold leading-none text-offwhite" data-id="element-1755" data-mp="1jbhlhok2jbsh">
                  {referralEconomics.friendPercent}%
                </dd>
                <dd className="mt-1 text-[13px] text-steel-400" data-id="element-1756" data-mp="2ur6a0kcz0ive">off, up to ${referralEconomics.friendMaxDiscount}</dd>
              </div>
              <div className="bg-grit-500 p-5 text-ink-950 sm:p-6" data-id="element-1757" data-mp="3q25qp33hvy4v">
                <dt className="text-[11px] font-bold uppercase tracking-[0.2em]" data-id="element-1758" data-mp="3uzxnobizyafx">You earn</dt>
                <dd className="mt-2 font-display text-5xl font-extrabold leading-none" data-id="element-1759" data-mp="3u5ut4du4jpf">${referralEconomics.referrerReward}</dd>
                <dd className="mt-1 text-[13px] font-semibold" data-id="element-1760" data-mp="8u0mha5fpdbj">per qualified order</dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:grid lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-20" data-id="element-1761" data-mp="csq6hlkz9t45">
          <div data-id="element-1762" data-mp="mjuchvsoe3mu">
            <h2 className="font-display text-3xl font-extrabold uppercase text-offwhite" data-id="element-1763" data-mp="mlf6nkb2vkk6">How it works</h2>
            <ol className="mt-6 grid gap-5" data-id="element-1764" data-mp="rifdhxfr4rcc">
              {referralSteps.map((step, index) => <li key={step.title} className="flex gap-4" data-id="element-1765" data-mp="wjlai7gg30ro" data-mpi={index}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-grit-500 font-display text-xl font-extrabold text-ink-950" data-id="element-1766" data-mp="10jvp28iayzi5">
                    {index + 1}
                  </span>
                  <span data-id="element-1767" data-mp="2h0ugnq1d5u90">
                    <span className="block font-display text-xl font-bold uppercase text-offwhite" data-id="element-1768" data-mp="2gzj87cwz3ybk">{step.title}</span>
                    <span className="mt-1 block text-[15px] text-steel-300" data-id="element-1769" data-mp="2c219efco6zha">{step.body}</span>
                  </span>
                </li>)}
            </ol>
          </div>
          <div className="mt-12 lg:mt-0" data-id="element-1770" data-mp="34ovhecsaw6dv">
            <h2 className="font-display text-3xl font-extrabold uppercase text-offwhite" data-id="element-1771" data-mp="34rzrtrwczycz">The rules</h2>
            <ul className="mt-6 grid gap-2.5 border-t border-white/10 pt-5" data-id="element-1772" data-mp="2ztg87y58myns">
              {referralRules.map(rule => <li key={rule} className="text-[14px] leading-relaxed text-steel-300" data-id="element-1773" data-mp="2q1sm8crga9tp">
                  {rule}
                </li>)}
            </ul>
            <div className="mt-6" data-id="element-1774" data-mp="3nihjdt5ggnwr">
              <ProgramLink href={REFERRAL_TERMS_URL} label="Program terms" data-id="element-1775" data-mp="3ipg3pixfckc4" />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter data-id="element-1776" data-mp="1gsxpib7bhob5" />
    </div>;
}